import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { openModal } from "../app/features/slices/modalSlice";
import store from "../app/store";
import CreatePasswordForm from "../components/auth/CreatePasswordForm";
import { initializeApp } from "../components/hook/Webinitalizer";
import { Paths } from "../constants/enums";
import { logoutUser, refreshAccessToken } from "../services/authService";
import { getToken } from "../utils/tokenUtils";

// Create Auth Context
const AuthContext = createContext();

// Auth Provider
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const refreshTimeout = useRef(null);
  const { t } = useTranslation();
  const scheduleTokenRefresh = () => {
    if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    const token = getToken();
    if (!token) return;

    const decoded = jwtDecode(token);
    const expiresIn = decoded.exp - Math.floor(Date.now() / 1000);
    if (expiresIn > 120) {
      refreshTimeout.current = setTimeout(async () => {
        await refreshAccessToken();
        scheduleTokenRefresh(); // Schedule next refresh
      }, (expiresIn - 120) * 1000);
    }
  };

  const checkPasswordChangeRequirement = (decoded) => {
    const needsChange = decoded?.isPasswordChanged !== "1";
    setRequiresPasswordChange(needsChange);

    if (needsChange) {
      dispatch(
        openModal({
          title: "profile.changepassword",
          content: <CreatePasswordForm enforceChange={true} />,
          closable: true,
        })
      );
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUser({
          id: decoded.Id,
          role: decoded[
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
          ],
          username: decoded.UserName,
          department: decoded.Department,
          isPasswordChanged: decoded.isPasswordChanged,
        });
        checkPasswordChangeRequirement(decoded);

        scheduleTokenRefresh();
        Cookies.set(
          "role",
          decoded[
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
          ]
        ); // 1 day expiry
      } catch (error) {
        logoutUser(null);
      }
    }
  }, []);

  useEffect(() => {
    if (
      user ||
      [Paths?.ADD_CUSTOMER, Paths?.UPDATE_CUSTOMER].includes(
        window?.location?.pathname
      )
    ) {
      initializeApp(user, dispatch, store.getState);
    }
  }, [user]);

  // Function to store tokens in cookies
  const setAuthData = (accessToken, refreshToken) => {
    Cookies.set("accessTokenJKT", accessToken, { expires: 1 });
    Cookies.set("refreshTokenJKT", refreshToken, { expires: 7 });

    const decoded = jwtDecode(accessToken);
    setUser({
      id: decoded.Id,
      role: decoded[
        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
      ],
      username: decoded.UserName,
    });
    checkPasswordChangeRequirement(decoded);

    scheduleTokenRefresh(); // Ensure token refresh is scheduled
  };

  const handleSuccessfulPasswordChange = () => {
    Cookies.remove("accessTokenJKT");
    Cookies.remove("refreshTokenJKT");
    Cookies.remove("role");
    navigate("/");
    window.location.reload(); // Ensure full re-initialization
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        requiresPasswordChange,
        setAuthData,
        handleSuccessfulPasswordChange,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook
export const useAuth = () => {
  return useContext(AuthContext);
};
